<script setup lang="ts">
import type { HTMLAttributes } from 'vue'

import { cn } from 'cn'
import { onBeforeUnmount, onMounted, useTemplateRef, watch } from 'vue'

import type { MessageScrollerButtonDirection } from './useMessageScroller'

import { SCROLL_KEYS, useMessageScrollerContext } from './useMessageScroller'

const props = withDefaults(defineProps<{
  class?: HTMLAttributes['class']
  preserveScrollOnPrepend?: boolean
}>(), {
  preserveScrollOnPrepend: true,
})

const {
  autoscrolling,
  handleResize,
  scrollableAttr,
  setPreserveScrollOnPrepend,
  setViewportElement,
  syncAfterScroll,
  userScrollIntent,
} = useMessageScrollerContext()

const viewportEl = useTemplateRef<HTMLElement>('viewport')

watch(() => props.preserveScrollOnPrepend, setPreserveScrollOnPrepend, { immediate: true })

function onWheel(event: WheelEvent) {
  if (event.deltaY !== 0)
    userScrollIntent(event.deltaY < 0 ? 'start' : 'end')
}

function onKeyDown(event: KeyboardEvent) {
  if (!SCROLL_KEYS.has(event.key))
    return

  const direction: MessageScrollerButtonDirection
    = event.key === 'ArrowUp'
      || event.key === 'Home'
      || event.key === 'PageUp'
      || (event.key === ' ' && event.shiftKey)
      ? 'start'
      : 'end'
  userScrollIntent(direction)
}

let previousTouchY: number | null = null

function onTouchStart(event: TouchEvent) {
  previousTouchY = event.touches[0]?.clientY ?? null
}

function onTouchMove(event: TouchEvent) {
  const touchY = event.touches[0]?.clientY
  if (touchY === undefined || previousTouchY === null)
    return

  const direction = touchY > previousTouchY ? 'start' : 'end'
  if (touchY !== previousTouchY)
    userScrollIntent(direction)
  previousTouchY = touchY
}

function onTouchEnd() {
  previousTouchY = null
}

let resizeObserver: ResizeObserver | null = null
let resizeFrame = 0

onMounted(() => {
  const viewport = viewportEl.value
  setViewportElement(viewport)
  if (!viewport || typeof ResizeObserver === 'undefined')
    return
  resizeObserver = new ResizeObserver(() => {
    window.cancelAnimationFrame(resizeFrame)
    resizeFrame = window.requestAnimationFrame(handleResize)
  })
  resizeObserver.observe(viewport)
})

onBeforeUnmount(() => {
  window.cancelAnimationFrame(resizeFrame)
  resizeObserver?.disconnect()
  resizeObserver = null
  setViewportElement(null)
})
</script>

<template>
  <div
    ref="viewport"
    data-slot="message-scroller-viewport"
    role="region"
    aria-label="Messages"
    :tabindex="0"
    :data-scrollable="scrollableAttr"
    :data-autoscrolling="autoscrolling ? '' : undefined"
    :class="cn(
      'size-full min-h-0 min-w-0 overflow-y-auto overscroll-contain data-[autoscrolling]:scrollbar-thumb-transparent data-[autoscrolling]:scrollbar-track-transparent',
      props.class,
    )"
    @scroll="syncAfterScroll()"
    @wheel="onWheel"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
    @keydown="onKeyDown"
  >
    <slot />
  </div>
</template>
